<template>
  <Container v-if="status === 'pending'">
    <Spinner />
  </Container>

  <Container v-if="news">
    <NewsEditor
      preview
      :content="news"
    />
    <auth-only :roles="[ UserRole.EDITOR, UserRole.MANAGER, UserRole.ADMINISTRATOR ]">
      <NuxtLink
        :to="localePath({
          name: 'news-news-edit',
          params: {
            news: news.id!!
          }
        })"
        class="bg-primary hover:bg-primary-highlighted active:bg-primary-selected
          text-white text-center rounded-md py-2 px-4 ml-auto font-semibold"
        :title="t('jc.view.news-news.editButton.title')"
      >
        {{ t('jc.view.news-news.editButton.label') }}
      </NuxtLink>
    </auth-only>
  </Container>
</template>

<script setup lang="ts">
import {type FileReference, type Tag, translate, translateNews} from "~/composables/charon";
import { UserRole } from "~/composables/charon";
import { generateText } from "@tiptap/core";
import { teaserExtensions, titleExtensions } from "~/tiptap/config";
import { FetchError } from "ofetch";

definePageMeta({
  ogType: 'article',
  nameKey: 'news-item'
})

const localePath = useLocalePath()
const { charon, listNews } = useCharon()
const route = useRoute()
const config = useRuntimeConfig()
const { t, locale } = useI18n()

const { status, data: news } = await useAsyncData(
  () => listNews({
    "slug[eq]": route.params.news,
    "locales[exists]": locale.value,
    size: 1,
  })
    .then(news => {
      if (news.length === 0) {
        showError({
          statusCode: 404,
          message: t(`jc.error.newsRequest.${404}.message`, { news: route.params.news }),
        })
      }
      return news[0]
    })
    .catch((reason: FetchError) => {
      const responseStatus = reason.response?.status ?? 500
      showError({
        statusCode: responseStatus,
        message: t(`jc.error.newsRequest.${responseStatus}.message`, { news: route.params.news }),
      })
      return null
    }),
  {
    watch: [ locale, () => route.params.news ]
  }
)

const transNews = computed(() => translateNews(news.value, locale.value))

const { data: reference } = await useAsyncData(
  async () => {
    if (transNews.value?.image) {
      return null
    }
    return charon<FileReference>(`/file-references/${transNews.value!.image}`)
  },
  {
    watch: [ () => transNews.value?.image ]
  }
)

// request tags in bulk
const { data: tags } = await useAsyncData(
  async () => {
    if (!news.value?.tags) {
      return []
    }
    return await charon<Tag[]>(`/tags`, {
      params: {
        'id[in]': news.value?.tags?.join(","),
      }
    })
  },
  {
    default: () => [] as Tag[],
    watch: [ () => news.value?.tags ]
  }
)

useHead(() => {
  if (!news.value || !transNews.value) {
    return {}
  }

  // generate the plain text title from (json) title
  let newsTitle = ""
  if (transNews.value?.title) {
    newsTitle = generateText(transNews.value.title, titleExtensions())
  }

  // generate the plain text teaser either from (json) teaser or (json) body
  let newsTeaser = ""
  if (transNews.value?.teaser) {
    newsTeaser = generateText(transNews.value.teaser, teaserExtensions())
  }
  if (!newsTeaser && transNews.value?.body) {
    newsTeaser = generateText(transNews.value.body, teaserExtensions())
  }

  const imageUrl = transNews.value.image
    ? `${config.public.charonUrl}/file-references/file/${transNews.value.image}`
    : "/images/component/charonFile/news_placeholder.png"

  const keywords = tags.value.map(tag => translate(tag.locales, locale.value)?.label)

  const now = new Date().toISOString()
  const modifiedDate = Date.parse(news.value.updateDate ?? now) > Date.parse(news.value.publishDate ?? now)
    ? news.value.updateDate
    : news.value.publishDate

  return {
    title: t(`jc.head.${String(route.meta.nameKey)}.withNewsTitle.title`, { newsTitle }),
    meta: [
      // Basic
      { name: 'description', content: newsTeaser },
      { name: 'keywords', content: keywords.join(", ") },
      // Open Graph Protocol
      { name: 'og:title', content: newsTitle },
      { name: 'og:description', content: newsTeaser },
      { name: 'og:image', content: imageUrl },
      { name: 'og:image:alt', content: translate(reference.value?.locales, locale.value)?.description },
      { name: 'og:article:published_time', content: news.value.publishDate },
      { name: 'og:article:modified_time', content: modifiedDate ?? now },
      { name: 'og:article:section', content: news.value.type.toLowerCase() },
      ...keywords.map(keyword => (
        { name: 'og:article:tag', content: keyword }
      )),
      // Twitter Card
      { name: 'twitter:title', content: newsTitle },
      { name: 'twitter:description', content: newsTeaser },
      { name: 'twitter:image', content: imageUrl },
    ]
  }
})
</script>
